package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import components.auth.RegistrationStep1_Email
import components.auth.RegistrationStep2_Verify
import components.auth.RegistrationStep3_Account
import components.auth.OnboardingWelcome
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

/**
 * Progressive Registration Flow
 *
 * 4-step guided registration process:
 * 1. Email Input - Collect email address
 * 2. Email Verification - Verify with 6-digit code
 * 3. Account Creation - Username + password with strength meter
 * 4. Welcome - Onboarding with feature highlights
 */
@Composable
fun RegisterScreen(
    onRegisterSuccess: () -> Unit,
    onNavigateToLogin: () -> Unit,
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()
    val coroutineScope = rememberCoroutineScope()

    // Track registration step (1-4)
    var currentStep by remember { mutableStateOf(1) }

    // Navigate on successful registration
    LaunchedEffect(state.isLoggedIn) {
        if (state.isLoggedIn) {
            // Move to welcome screen or complete
            if (currentStep < 4) {
                currentStep = 4
            }
        }
    }

    // Back button handler
    fun onBack() {
        when (currentStep) {
            1 -> onNavigateToLogin()
            2 -> {
                currentStep = 1
                // Clear verification state
                authViewModel.onEvent(AuthEvent.OnVerificationCodeChanged(""))
            }
            3 -> currentStep = 2
            4 -> currentStep = 3
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        // Back button (not shown on step 1)
        if (currentStep > 1) {
            Button({
                classes(AppStyleSheet.backButton)
                onClick { onBack() }
            }) { Text("← 戻る") }
        }

        when (currentStep) {
            1 -> {
                // Step 1: Email Input
                RegistrationStep1_Email(
                    email = state.emailInput,
                    onEmailChange = { email ->
                        authViewModel.onEvent(AuthEvent.OnEmailChanged(email))
                    },
                    onSendCode = {
                        // Store email for later steps and send verification code
                        authViewModel.onEvent(AuthEvent.OnEmailChanged(state.emailInput))
                        coroutineScope.launch {
                            kotlinx.coroutines.delay(300)
                            authViewModel.onEvent(AuthEvent.OnSendVerificationCode)
                        }
                        // Move to step 2 after code is sent
                        if (state.verificationCodeSent) {
                            currentStep = 2
                        }
                    },
                    isLoading = state.isLoading,
                    error = state.errorMessage,
                    onCancel = onNavigateToLogin
                )

                // Watch for successful code send to advance
                LaunchedEffect(state.verificationCodeSent) {
                    if (state.verificationCodeSent) {
                        currentStep = 2
                    }
                }
            }

            2 -> {
                // Step 2: Email Verification
                RegistrationStep2_Verify(
                    email = state.emailInput,
                    code = state.verificationCodeInput,
                    onCodeChange = { code ->
                        authViewModel.onEvent(AuthEvent.OnVerificationCodeChanged(code))
                    },
                    onVerify = {
                        authViewModel.onEvent(AuthEvent.OnVerifyEmailSubmit)
                    },
                    onResend = {
                        authViewModel.onEvent(AuthEvent.OnSendVerificationCode)
                    },
                    onChangeEmail = {
                        currentStep = 1
                    },
                    isLoading = state.isLoading,
                    error = state.errorMessage,
                    onSendCode = {
                        authViewModel.onEvent(AuthEvent.OnSendVerificationCode)
                    }
                )

                // Watch for successful verification to advance
                LaunchedEffect(state.emailVerified) {
                    if (state.emailVerified) {
                        // Store email and verification code for final registration
                        // We need to store these in the state for use in step 3
                        currentStep = 3
                    }
                }
            }

            3 -> {
                // Step 3: Account Creation (Username + Password)
                RegistrationStep3_Account(
                    email = state.emailInput,
                    username = state.usernameInput,
                    password = state.passwordInput,
                    onUsernameChange = { username ->
                        authViewModel.onEvent(AuthEvent.OnUsernameChanged(username))
                    },
                    onPasswordChange = { password ->
                        authViewModel.onEvent(AuthEvent.OnPasswordChanged(password))
                    },
                    onComplete = {
                        // Store temporary email and verification code
                        // and complete registration
                        authViewModel.onEvent(AuthEvent.OnCompleteRegistration)
                    },
                    onBack = {
                        currentStep = 2
                    },
                    isLoading = state.isLoading,
                    error = state.errorMessage,
                    authViewModel = authViewModel
                )

                // Watch for successful registration to advance
                LaunchedEffect(state.isLoggedIn) {
                    if (state.isLoggedIn) {
                        currentStep = 4
                    }
                }
            }

            4 -> {
                // Step 4: Welcome / Onboarding
                val username = state.user?.username ?: "ユーザー"
                OnboardingWelcome(
                    username = username,
                    onStart = {
                        onRegisterSuccess()
                    },
                    onSetupTotp = {
                        // Navigate to TOTP setup (will be handled by parent)
                        onRegisterSuccess()
                    }
                )
            }
        }
    }
}
